<template>
  <div class="file-upload">
    <!-- Drag and Drop Area -->
    <div
      class="file-upload-area"
      :class="{ dragover: isDragOver }"
      @click="triggerFileInput"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :multiple="multiple"
        :accept="accept"
        @change="handleFileSelect"
        style="display: none"
      />

      <div v-if="!hasFiles" class="upload-placeholder">
        <i class="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
        <h6>Drop files here or click to browse</h6>
        <p class="text-muted mb-0">
          {{ uploadText }}
        </p>
      </div>

      <!-- File Preview -->
      <div v-if="hasFiles" class="file-preview">
        <div
          v-for="(file, index) in previewFiles"
          :key="index"
          class="file-preview-item"
        >
          <!-- Image Preview -->
          <div v-if="isImage(file)" class="position-relative">
            <img
              :src="getFileUrl(file)"
              :alt="`Preview ${index + 1}`"
              class="file-preview-image"
            />
            <!-- Upload Progress Overlay -->
            <div
              v-if="uploadingFiles.has(index)"
              class="upload-progress-overlay"
            >
              <div class="upload-progress-circle">
                <div class="upload-progress-text">
                  {{ uploadingFiles.get(index) }}%
                </div>
              </div>
            </div>
            <button
              type="button"
              class="file-remove-btn"
              @click.stop="removeFile(index)"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Video Preview -->
          <div v-else-if="isVideo(file)" class="position-relative">
            <video :src="getFileUrl(file)" class="file-preview-video" controls>
              Your browser does not support the video tag.
            </video>
            <!-- Upload Progress Overlay -->
            <div
              v-if="uploadingFiles.has(index)"
              class="upload-progress-overlay"
            >
              <div class="upload-progress-circle">
                <div class="upload-progress-text">
                  {{ uploadingFiles.get(index) }}%
                </div>
              </div>
            </div>
            <button
              type="button"
              class="file-remove-btn"
              @click.stop="removeFile(index)"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- PDF Preview -->
          <div
            v-else-if="isPdf(file)"
            class="file-preview-pdf position-relative"
          >
            <div class="pdf-icon">
              <i class="fas fa-file-pdf fa-3x text-danger"></i>
            </div>
            <div class="pdf-name">
              {{ getFileName(file) }}
            </div>
            <button
              type="button"
              class="file-remove-btn"
              @click.stop="removeFile(index)"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Other File Types -->
          <div v-else class="file-preview-generic position-relative">
            <div class="generic-icon">
              <i class="fas fa-file fa-3x text-muted"></i>
            </div>
            <div class="file-name">
              {{ getFileName(file) }}
            </div>
            <button
              type="button"
              class="file-remove-btn"
              @click.stop="removeFile(index)"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Progress -->
    <div v-if="isUploading" class="upload-progress mt-2">
      <div class="progress">
        <div
          class="progress-bar"
          role="progressbar"
          :style="{ width: uploadProgress + '%' }"
        >
          {{ uploadProgress }}%
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="error" class="alert alert-danger mt-2" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
    </div>
  </div>
</template>

<script>
import largeFileUploadService from "@/services/largeFileUploadService";

export default {
  name: "FileUpload",
  props: {
    multiple: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: 1,
    },
    accept: {
      type: String,
      default: "*/*",
    },
    existingFiles: {
      type: Array,
      default: () => [],
    },
    tripId: {
      type: String,
      default: null,
    },
  },
  emits: ["files-selected"],
  data() {
    return {
      selectedFiles: [],
      isDragOver: false,
      isUploading: false,
      uploadProgress: 0,
      error: null,
      uploadingFiles: new Map(),
    };
  },
  computed: {
    previewFiles() {
      // Combine existing files (URLs) and new files (File objects)
      const existing = Array.isArray(this.existingFiles)
        ? this.existingFiles
        : [];
      const selected = Array.isArray(this.selectedFiles)
        ? this.selectedFiles
        : [];
      return [...existing, ...selected];
    },

    hasFiles() {
      return this.previewFiles.length > 0;
    },

    uploadText() {
      if (this.accept.includes("image")) {
        return `Select ${this.multiple ? "images" : "an image"} to upload`;
      } else if (this.accept.includes("video")) {
        return `Select ${this.multiple ? "videos" : "a video"} to upload`;
      } else if (this.accept.includes("pdf")) {
        return `Select ${this.multiple ? "PDF files" : "a PDF file"} to upload`;
      }
      return `Select ${this.multiple ? "files" : "a file"} to upload`;
    },
  },
  watch: {
    existingFiles: {
      handler(newVal) {
        // Reset selected files when existing files change externally
        // This helps sync when parent updates the prop
        if (Array.isArray(newVal)) {
          // Keep selected files that haven't been uploaded yet
          this.selectedFiles = this.selectedFiles.filter(
            file => file instanceof File
          );
        }
      },
      deep: true
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async handleFileSelect(event) {
      const files = Array.from(event.target.files);

      // Clear the input to prevent duplicate processing
      event.target.value = "";

      // Process files one by one for better control
      for (const file of files) {
        await this.addSingleFile(file);
      }
    },

    // Add a single file with proper validation and upload
    async addSingleFile(file) {
      this.error = null;

      // Validate file count
      const totalFiles =
        this.existingFiles.length + this.selectedFiles.length + 1;
      if (totalFiles > this.maxFiles) {
        this.error = `Maximum ${this.maxFiles} file${
          this.maxFiles > 1 ? "s" : ""
        } allowed`;
        return;
      }

      // Validate file type and size based on accept prop
      if (!this.validateFile(file)) {
        return; // Error is set in validateFile
      }

      // Add file to selectedFiles
      if (this.multiple) {
        this.selectedFiles.push(file);
      } else {
        this.selectedFiles = [file];
      }

      // Validate media limits
      if (!this.validateMediaLimits()) {
        // Remove the file that was just added
        if (this.multiple) {
          this.selectedFiles.pop();
        } else {
          this.selectedFiles = [];
        }
        return;
      }

      // Always upload files immediately using presigned URLs
      await this.uploadSingleFile(file);
    },

    // Upload a single file using presigned URL
    async uploadSingleFile(file) {
      const fileIndex = this.selectedFiles.indexOf(file);

      this.isUploading = true;
      this.uploadingFiles.set(fileIndex, 0);

      try {
        // Ensure PDF files have correct MIME type
        if (/\.pdf$/i.test(file.name) && (!file.type || file.type === '')) {
          file = new File([file], file.name, { type: 'application/pdf' });
        }

        const result = await largeFileUploadService.uploadLargeFile(
          file,
          this.tripId,
          (fileProgress) => {
            this.uploadingFiles.set(fileIndex, fileProgress);
            this.uploadProgress = fileProgress;
          }
        );

        if (result.success) {
          // Remove the file from selectedFiles and add URL to existingFiles
          this.selectedFiles.splice(fileIndex, 1);
          this.existingFiles.push(result.fileUrl);
          this.emitFiles();
        } else {
          this.error = `Failed to upload ${file.name}: ${result.error}`;
          console.error("Upload failed:", result.error);
          // Remove the failed file
          this.selectedFiles.splice(fileIndex, 1);
        }
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        this.error = `Failed to upload ${file.name}: ${error.message}`;
        // Remove the failed file
        this.selectedFiles.splice(fileIndex, 1);
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.uploadingFiles.clear();
      }
    },

    handleDragOver() {
      this.isDragOver = true;
    },

    handleDragLeave() {
      this.isDragOver = false;
    },

    async handleDrop(event) {
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);

      // Process files one by one for better control
      for (const file of files) {
        await this.addSingleFile(file);
      }
    },

    processFiles(files) {
      this.error = null;

      // Validate file count
      const totalFiles =
        this.existingFiles.length + this.selectedFiles.length + files.length;
      if (totalFiles > this.maxFiles) {
        this.error = `Maximum ${this.maxFiles} file${
          this.maxFiles > 1 ? "s" : ""
        } allowed`;
        return;
      }

      // Validate file types and sizes
      for (const file of files) {
        if (!this.validateFile(file)) {
          return; // Error is set in validateFile
        }
      }

      // Add files
      if (this.multiple) {
        this.selectedFiles.push(...files);
      } else {
        this.selectedFiles = [files[0]];
      }

      // Validate media limits
      if (!this.validateMediaLimits()) {
        // Remove the files that were just added
        if (this.multiple) {
          this.selectedFiles.splice(-files.length);
        } else {
          this.selectedFiles = [];
        }
        return;
      }

      this.emitFiles();
    },

    validateFile(file) {
      // Use context-aware validation based on accept prop
      const validation = this.validateFileByAccept(file);
      if (!validation.isValid) {
        this.error = validation.errors.join(", ");
        return false;
      }

      // Check file type matches accept prop
      if (this.accept !== "*/*" && !this.isAcceptedType(file)) {
        this.error = `Invalid file type. Accepted types: ${this.accept}`;
        return false;
      }

      return true;
    },

    // Context-aware file validation based on accept prop
    validateFileByAccept(file) {
      const errors = [];

      // Determine what types are expected based on accept prop
      const isImageExpected = this.accept.includes('image/') || this.accept.includes('image/*');
      const isVideoExpected = this.accept.includes('video/') || this.accept.includes('video/*');
      const isPdfExpected = this.accept.includes('.pdf') || this.accept.includes('application/pdf');

      // Only validate file size for the expected types
      if (file.type.startsWith('image/')) {
        if (!isImageExpected && (isVideoExpected || isPdfExpected)) {
          errors.push(`Invalid file type. Expected: ${this.getExpectedTypesMessage()}`);
          return { isValid: false, errors };
        }
        const maxImageSize = 50 * 1024 * 1024; // 50MB
        if (file.size > maxImageSize) {
          errors.push(`Image file too large: ${this.formatFileSize(file.size)}. Maximum size: 50MB`);
        }
      } else if (file.type.startsWith('video/')) {
        if (!isVideoExpected && (isImageExpected || isPdfExpected)) {
          errors.push(`Invalid file type. Expected: ${this.getExpectedTypesMessage()}`);
          return { isValid: false, errors };
        }
        const maxVideoSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxVideoSize) {
          errors.push(`Video file too large: ${this.formatFileSize(file.size)}. Maximum size: 100MB`);
        }
      } else if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
        if (!isPdfExpected && (isImageExpected || isVideoExpected)) {
          errors.push(`Invalid file type. Expected: ${this.getExpectedTypesMessage()}`);
          return { isValid: false, errors };
        }
        const maxPdfSize = 25 * 1024 * 1024; // 25MB
        if (file.size > maxPdfSize) {
          errors.push(`PDF file too large: ${this.formatFileSize(file.size)}. Maximum size: 25MB`);
        }
      } else if (!file.type || file.type === '') {
        // Handle files without MIME type (check by extension)
        if (/\.pdf$/i.test(file.name)) {
          if (!isPdfExpected && (isImageExpected || isVideoExpected)) {
            errors.push(`Invalid file type. Expected: ${this.getExpectedTypesMessage()}`);
            return { isValid: false, errors };
          }
          const maxPdfSize = 10 * 1024 * 1024; // 10MB
          if (file.size > maxPdfSize) {
            errors.push(`PDF file too large: ${this.formatFileSize(file.size)}. Maximum size: 10MB`);
          }
        } else {
          errors.push(`Invalid file type. Expected: ${this.getExpectedTypesMessage()}`);
        }
      } else {
        // Unknown file type
        errors.push(`Invalid file type. Expected: ${this.getExpectedTypesMessage()}`);
      }

      return {
        isValid: errors.length === 0,
        errors
      };
    },

    // Get human-readable expected types message based on accept prop
    getExpectedTypesMessage() {
      const isImageExpected = this.accept.includes('image/') || this.accept.includes('image/*');
      const isVideoExpected = this.accept.includes('video/') || this.accept.includes('video/*');
      const isPdfExpected = this.accept.includes('.pdf') || this.accept.includes('application/pdf');

      const types = [];
      if (isImageExpected) types.push('Images (JPG, PNG, GIF, WebP, HEIC)');
      if (isVideoExpected) types.push('Videos (MP4, MOV, AVI, WMV, FLV, WebM)');
      if (isPdfExpected) types.push('PDF files');

      return types.length > 0 ? types.join(', ') : 'Valid files';
    },

    // New method to validate media limits
    validateMediaLimits() {
      const imageFiles = this.selectedFiles.filter((f) => this.isImage(f));
      const videoFiles = this.selectedFiles.filter((f) => this.isVideo(f));

      // For reviews: max 3 images OR 1 video
      if (this.accept.includes("image/") && this.accept.includes("video/")) {
        if (imageFiles.length > 3) {
          this.error = `Too many images: ${imageFiles.length}. Maximum 3 images allowed.`;
          return false;
        }
        if (videoFiles.length > 1) {
          this.error = `Too many videos: ${videoFiles.length}. Maximum 1 video allowed.`;
          return false;
        }
        if (imageFiles.length > 0 && videoFiles.length > 0) {
          this.error =
            "Cannot upload both images and videos. Choose either up to 3 images OR 1 video.";
          return false;
        }
      }

      // For trips: max 10 images
      if (this.accept === "image/*" && imageFiles.length > 10) {
        this.error = `Too many images: ${imageFiles.length}. Maximum 10 images allowed.`;
        return false;
      }

      return true;
    },

    isAcceptedType(file) {
      if (this.accept.includes("*")) return true;

      const acceptedTypes = this.accept.split(",").map((type) => type.trim());
      return acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          // Check file extension
          const extension = type.toLowerCase();
          const fileName = file.name.toLowerCase();
          if (fileName.endsWith(extension)) {
            return true;
          }
          // Also check MIME type for common extensions
          if (extension === ".pdf" && (file.type === "application/pdf" || file.type === "" || !file.type)) {
            return true;
          }
          return false;
        }

        // Handle specific MIME type patterns
        if (type === "image/*") {
          return this.isImage(file);
        }
        if (type === "video/*") {
          return this.isVideo(file);
        }
        if (type === "application/pdf") {
          return file.type === "application/pdf" || /\.pdf$/i.test(file.name) || file.type === "" || !file.type;
        }

        return file.type.match(new RegExp(type.replace("*", ".*")));
      });
    },

    getMaxFileSize(file) {
      if (file.type.startsWith("image/")) return 5 * 1024 * 1024; // 5MB
      if (file.type.startsWith("video/")) return 100 * 1024 * 1024; // 100MB
      if (file.type === "application/pdf") return 25 * 1024 * 1024; // 25MB
      return 25 * 1024 * 1024; // 25MB default
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    removeFile(index) {
      const existingCount = this.existingFiles.length;

      if (index < existingCount) {
        // Remove from existing files - create a new array to avoid mutating prop
        const updatedExisting = [...this.existingFiles];
        updatedExisting.splice(index, 1);
        // Emit the updated list so parent can update
        this.$emit("files-selected", [...updatedExisting, ...this.selectedFiles]);
      } else {
        // Remove from selected files
        this.selectedFiles.splice(index - existingCount, 1);
        this.emitFiles();
      }
    },

    emitFiles() {
      // Emit all files (existing + new) so parent can track what's being kept
      this.$emit("files-selected", this.previewFiles);
    },

    isImage(file) {
      if (typeof file === "string") {
        // Check if it's an S3 URL with image extension
        return /\.(jpg|jpeg|png|gif|webp|heic)(\?.*)?$/i.test(file);
      }
      // More strict image type validation
      const validImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/heic",
      ];
      return validImageTypes.includes(file.type);
    },

    isVideo(file) {
      if (typeof file === "string") {
        return /\.(mp4|avi|mov|wmv|flv|webm)(\?.*)?$/i.test(file);
      }
      // More comprehensive video type validation
      const validVideoTypes = [
        "video/mp4",
        "video/mov",
        "video/avi",
        "video/wmv",
        "video/flv",
        "video/webm",
        "video/quicktime",
      ];
      return (
        validVideoTypes.includes(file.type) || file.type.startsWith("video/")
      );
    },

    isPdf(file) {
      if (typeof file === "string") {
        return /\.pdf$/i.test(file);
      }
      return file.type === "application/pdf";
    },

    getFileUrl(file) {
      if (typeof file === "string") {
        return file; // Already a URL
      }
      return URL.createObjectURL(file);
    },

    getFileName(file) {
      if (typeof file === "string") {
        return file.split("/").pop();
      }
      return file.name;
    },
  },
};
</script>

<style scoped>
.file-upload-area {
  border: 2px dashed #ced4da;
  border-radius: var(--border-radius);
  padding: 2rem;
  text-align: center;
  background: var(--light-color);
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-upload-area:hover {
  border-color: var(--primary-color);
  background: rgba(44, 90, 160, 0.05);
}

.file-upload-area.dragover {
  border-color: var(--primary-color);
  background: rgba(44, 90, 160, 0.1);
}

.upload-placeholder h6 {
  margin-bottom: 0.5rem;
  color: var(--dark-color);
}

.file-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
  justify-content: center;
}

.file-preview-item {
  position: relative;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
}

.file-preview-image,
.file-preview-video {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: var(--border-radius);
}

.file-preview-pdf,
.file-preview-generic {
  width: 120px;
  height: 120px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: var(--border-radius);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.pdf-name,
.file-name {
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
  word-break: break-word;
  max-width: 100%;
}

.file-remove-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: var(--danger-color);
  color: white;
  border: none;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.file-remove-btn:hover {
  background: #c82333;
  transform: scale(1.1);
}

.upload-progress .progress {
  height: 20px;
}

.progress-bar {
  background-color: var(--primary-color);
  transition: width 0.3s ease;
}

@media (max-width: 576px) {
  .file-upload-area {
    padding: 1rem;
    min-height: 120px;
  }

  .file-preview-image,
  .file-preview-video,
  .file-preview-pdf,
  .file-preview-generic {
    width: 100px;
    height: 100px;
  }
}

/* Upload Progress Overlay */
.upload-progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius);
}

.upload-progress-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #007bff;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 1.5s ease-in-out infinite;
}

.upload-progress-text {
  color: white;
  font-weight: bold;
  font-size: 12px;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
